/** @jsxImportSource @emotion/react */

import React, {
  createContext,
  ReactNode,
  Suspense,
  useState,
  useEffect,
  useRef,
} from 'react';

import { useHistory, useLocation } from 'react-router-dom';
import Loader from '../components/loader';
import Overlay from '../overlays/overlay';
import AlertOverlay, { IAlertOverlay } from '../overlays/alertOverlay';
import IFrameOverlay, { IIFrameOverlay } from '../overlays/iFrameOverlay';
import SplashOverlay, { ISplashOverlay } from '../overlays/splashOverlay';
import StripeCheckoutOverlay, {
  IStripeCheckoutOverlay,
} from '../overlays/stripeCheckoutOverlay';
import StripeManageCardOverlay, {
  IStripeManageCardOverlay,
} from '../overlays/stripeManageCardOverlay';
import BankIdOverlay, { IBankIdOverlay } from '../overlays/bankIdOverlay';
import SwedishEidOverlay, {
  ISwedishEidOverlay,
} from '../overlays/swedishEidOverlay';
import CallingScreenOverlay, {
  ICallingScreenOverlay,
} from '../overlays/callingScreenOverlay';
import ActionListOverlay, {
  IActionListOverlay,
} from '../overlays/actionListOverlay';
import IconAlertOverlay, {
  IIconAlertOverlay,
} from '../overlays/iconAlertOverlay';
import ImagePreviewOverlay, {
  IImagePreviewOverlay,
} from '../overlays/imagePreviewOverlay';
import type { IPdfPreviewOverlay } from '../overlays/pdfPreviewOverlay';
import type { IVideoOverlay } from '../overlays/videoOverlay';
import lazyWithLogging from '../utils/lazyWithLogging';

const PdfPreviewOverlay = React.lazy(
  lazyWithLogging(
    () =>
      import(
        /* webpackChunkName: "overlay-pdf" */ '../overlays/pdfPreviewOverlay'
      )
  )
);
const VideoOverlay = React.lazy(
  lazyWithLogging(
    () =>
      import(/* webpackChunkName: "overlay-video" */ '../overlays/videoOverlay')
  )
);

export interface IBaseOverlay {
  // Optional identifier set by the presenter. Lets callers verify that the
  // overlay currently on screen is the specific one they presented (e.g.
  // before calling `dismiss()` from a separate code path).
  id?: string;
  persistent?: boolean;
  dismissible?: boolean;
  noWidthConstrain?: boolean;
  fullscreenOnMobile?: boolean;
  onDismiss?: () => void;
}

interface IAlert {
  type: 'ALERT';
  data: IAlertOverlay;
}

interface IIconAlert {
  type: 'ICON_ALERT';
  data: IIconAlertOverlay;
}

interface IActionList {
  type: 'ACTION_LIST';
  data: IActionListOverlay;
}

interface IIFrame {
  type: 'IFRAME';
  data: IIFrameOverlay;
}

interface ISplash {
  type: 'SPLASH';
  data: ISplashOverlay;
}

interface IStripeCheckout {
  type: 'STRIPE_CHECKOUT';
  data: IStripeCheckoutOverlay;
}

interface IStripeManageCard {
  type: 'STRIPE_MANAGE_CARD';
  data: IStripeManageCardOverlay;
}

interface IBankId {
  type: 'BANK_ID';
  data: IBankIdOverlay;
}

interface ISwedishEid {
  type: 'SWEDISH_EID';
  data: ISwedishEidOverlay;
}

interface ICallingScreen {
  type: 'CALLING_SCREEN';
  data: ICallingScreenOverlay;
}

interface IImagePreview {
  type: 'IMAGE_PREVIEW';
  data: IImagePreviewOverlay;
}

interface IPdfPreview {
  type: 'PDF_PREVIEW';
  data: IPdfPreviewOverlay;
}

interface IVideo {
  type: 'VIDEO';
  data: IVideoOverlay;
}

export type IOverlay =
  | IAlert
  | IIconAlert
  | IActionList
  | ISplash
  | IIFrame
  | IStripeCheckout
  | IStripeManageCard
  | IBankId
  | ISwedishEid
  | ICallingScreen
  | IImagePreview
  | IPdfPreview
  | IVideo;

export interface IOverlayContext {
  presentOverlay: (modalData: IOverlay) => void;
  dismissOverlay: () => void;
  getCurrentOverlay: () => IOverlay | undefined;
}

export const OverlayContext = createContext({} as IOverlayContext);

interface IOverlayContextProvider {
  children: ReactNode;
}

export const OverlayContextProvider = ({
  children,
}: IOverlayContextProvider) => {
  const location = useLocation();
  const history = useHistory();

  const [isOpen, setIsOpen] = useState(false);
  const [overlay, setOverlay] = useState<IOverlay>();
  // Mirrors of the state above, updated synchronously so callers can read the
  // current overlay in the same tick they presented or dismissed it, before
  // React has rendered the batched update.
  const overlayRef = useRef<IOverlay | undefined>(overlay);
  const isOpenRef = useRef(isOpen);
  // An overlay can be presented during the commit of a route change, e.g. by a
  // post-navigation internal url. The route-change dismissal below must leave
  // such an overlay alone, so remember which location it belongs to.
  const overlayLocationRef = useRef(history.location);

  const presentOverlay = (overlayArg: IOverlay) => {
    overlayRef.current = overlayArg;
    overlayLocationRef.current = history.location;
    isOpenRef.current = true;
    setOverlay(overlayArg);
    setIsOpen(true);
  };

  const dismissOverlay = () => {
    isOpenRef.current = false;
    setIsOpen(false);

    if (overlayRef.current?.data.onDismiss) {
      overlayRef.current.data.onDismiss();
    }
  };

  const getCurrentOverlay = () => {
    return isOpenRef.current ? overlayRef.current : undefined;
  };

  const getOverlayComponent = () => {
    switch (overlay?.type) {
      case 'ALERT':
        return <AlertOverlay {...overlay.data} />;
      case 'ICON_ALERT':
        return <IconAlertOverlay {...overlay.data} />;
      case 'ACTION_LIST':
        return <ActionListOverlay {...overlay.data} />;
      case 'IFRAME':
        return <IFrameOverlay {...overlay.data} />;
      case 'SPLASH':
        return <SplashOverlay {...overlay.data} />;
      case 'STRIPE_CHECKOUT':
        return <StripeCheckoutOverlay {...overlay.data} />;
      case 'STRIPE_MANAGE_CARD':
        return <StripeManageCardOverlay {...overlay.data} />;
      case 'BANK_ID':
        return <BankIdOverlay {...overlay.data} />;
      case 'SWEDISH_EID':
        return <SwedishEidOverlay {...overlay.data} />;
      case 'CALLING_SCREEN':
        return <CallingScreenOverlay {...overlay.data} />;
      case 'IMAGE_PREVIEW':
        return <ImagePreviewOverlay {...overlay.data} />;
      case 'PDF_PREVIEW':
        return <PdfPreviewOverlay {...overlay.data} />;
      case 'VIDEO':
        return <VideoOverlay {...overlay.data} />;
      default:
        break;
    }
  };

  // Dismiss the overlay on route changes unless
  // set as persistent.
  useEffect(() => {
    if (
      overlayRef?.current &&
      !overlayRef?.current?.data.persistent &&
      overlayLocationRef.current !== location
    ) {
      dismissOverlay();
    }
  }, [location]);

  return (
    <OverlayContext.Provider
      value={{
        presentOverlay,
        dismissOverlay,
        getCurrentOverlay,
      }}
    >
      {children}
      <Overlay
        open={isOpen}
        dismissible={overlay?.data.dismissible}
        fullscreenOnMobile={overlay?.data.fullscreenOnMobile}
        constrainWidth={!overlay?.data.noWidthConstrain}
        onRequestClose={() => {
          isOpenRef.current = false;
          setIsOpen(false);
        }}
      >
        <Suspense
          fallback={
            <div role='status' aria-label='Loading'>
              <Loader show transparent />
            </div>
          }
        >
          {getOverlayComponent()}
        </Suspense>
      </Overlay>
    </OverlayContext.Provider>
  );
};
